import { get } from 'lodash';
import { isIpV4Valid, isIpV6Valid } from 'core/util/ip';
import pluralize from '@kentik/ui-shared/util/pluralize';

const getTraceCount = (group) =>
  Array.from(group.keys()).reduce(
    (traceCount, aID) =>
      (traceCount += Array.from(group.get(aID).keys()).reduce(
        (probeCount, tID) => (probeCount += group.get(aID).get(tID).size),
        0
      )),
    0
  );

const inferAgentIPBasedOnTarget = (data, agent) => {
  const { target = {} } = data.traces.find((trace) => trace?.target?.target_ip !== '::') || {};
  const { target_ip = '' } = target;
  const isTargetIPv4 = isIpV4Valid(target_ip);
  const isTargetIPv6 = isIpV6Valid(target_ip);
  const ipv4 = get(agent, 'metadata.public_ipv4_addresses[0].value');
  const ipv6 = get(agent, 'metadata.public_ipv6_addresses[0].value');

  if (isTargetIPv4 && ipv4) {
    return ipv4;
  }

  if (isTargetIPv6 && ipv6) {
    return ipv6;
  }

  return agent.ip;
};

const getAgent = ({ $syn, id, data, options }) => {
  if ($syn && id) {
    // try to pull from the historically accurate agent data first
    const config = options?.agents?.[id];
    const agentModel = $syn.agents.forge(config || { id }, { deserialize: false });
    // node/models use asn/as_name and the api returns asn_id/asn_name
    const { city, country, lat, long, region, asn, asn_id, as_name, asn_name, ...restAgent } = agentModel.toJS();
    const agent = {
      ...restAgent,
      agent_icon: agentModel?.iconUrl,
      icon: agentModel?.icon,
      asn: { id: asn || asn_id, name: as_name || asn_name },
      geo: {
        city: {
          name: city,
          latitude: lat,
          longitude: long
        },
        country: {
          code: country,
          name: country
        },
        region: {
          name: region
        }
      }
    };

    if (data?.traces) {
      const { agent_ip } = data.traces.find((trace) => trace.agent_ip !== '::') || {};
      agent.ip = agent_ip || inferAgentIPBasedOnTarget(data, agent);
    }

    return agent;
  }

  return {};
};

// move and util
const aggregateNumbers = (numbers) => {
  const count = numbers.length;

  const { total, max, min } = numbers.reduce(
    (acc, curr) => {
      if (curr > 0) {
        acc.total += curr;
        acc.max = curr > acc.max ? curr : acc.max;
        acc.min = curr < acc.min ? curr : acc.min;
      }

      return acc;
    },
    {
      total: 0,
      max: numbers[0],
      min: numbers[0]
    }
  );

  return {
    count,
    min,
    max,
    average: total / count
  };
};

const getLocationName = (geo) => {
  const { country, region, city } = geo || {};
  const parts = [];

  [city, region, country].forEach((area) => {
    const name = (area && area.name) || '';
    if (name && name !== '-' && parts[parts.length - 1] !== name) {
      parts.push(name);
    }
  });

  return parts.length ? parts.join(', ') : null;
};

const calculateNodeMetrics = (hops = [], { withLoss } = {}) => {
  const { losses, latencies, minExpectedLatencies, successes } = hops.reduce(
    (acc, curr) => {
      const { loss, latency, minExpectedLatency } = curr;

      if (loss) {
        acc.losses += 1;
      } else {
        acc.successes += 1;
      }

      if (latency) {
        acc.latencies.push(latency);
      }

      if (minExpectedLatency) {
        acc.minExpectedLatencies.push(minExpectedLatency);
      }

      return acc;
    },
    {
      losses: 0,
      latencies: [],
      minExpectedLatencies: [],
      successes: 0
    }
  );

  let loss;
  if (withLoss) {
    loss = (losses / hops.length) * 100;
  }

  let success;
  if (withLoss) {
    success = (successes / hops.length) * 100;
  }

  return {
    loss,
    success,
    successes,
    total: hops.length,
    latency: latencies.length
      ? {
          ...aggregateNumbers(latencies),
          minExpectedLatency: minExpectedLatencies.length ? aggregateNumbers(minExpectedLatencies).average : null
        }
      : null
  };
};

const getIPData = (data) => {
  const ip = data.id;
  const {
    ingress_interface: ingress,
    egress_interface: egress,
    geo,
    asn,
    dns,
    site
  } = (data.hops || []).find((hop) => hop.ip === ip) || {};
  const location = getLocationName(geo);
  const nodeMetrics = calculateNodeMetrics(data.hops, { withLoss: false });

  return {
    name: ip || (ingress && ingress.interface_ip),
    nodeMetrics,
    ingress,
    egress,
    device: ingress || egress || null,
    asn,
    dns,
    location,
    site,
    geo
  };
};

const getTimeoutData = (data) => {
  const hopsCount = data.hops.length;

  return {
    name: `${hopsCount} Unidentified ${pluralize('Node', hopsCount)}`
  };
};

const getAgentData = (data, options) => {
  const { $syn } = options || {};
  const agent = getAgent({ $syn, id: data.id, data, options });
  const location = getLocationName(agent.geo);
  const { ingress, egress, device, asn } = agent;

  return {
    name: agent.agent_alias || `Agent ${agent.id}`,
    asn: agent.asn || asn || {},
    agent,
    ingress,
    egress,
    device,
    location,
    site: agent.site,
    geo: agent.geo
  };
};

const getTargetData = (data, options) => {
  const { $syn } = options || {};
  const agentMatch = data.hops.find((hop) => hop.agent && hop.ip === data.id) || {};
  const agent = getAgent({ $syn, id: agentMatch.agent?.id, options });
  const ip = data.id;
  const ipData = getIPData({ ...data, id: ip });
  const nodeMetrics = calculateNodeMetrics(data.hops, { withLoss: true });

  return {
    name: agent.agent_alias || ip,
    title: 'Target',
    asn: agent.asn || ipData.asn || {},
    agent,
    nodeMetrics,
    ingress: ipData.ingress,
    egress: ipData.egress,
    location: getLocationName(agent.geo) || ipData.location,
    site: agent.site || ipData.site,
    geo: agent.geo || ipData.geo
  };
};

const getClosedPathData = (data) => {
  const { group } = data;
  const agentGroup = group.entries().next().value;
  const agentID = agentGroup[0];
  const targetGroup = agentGroup[1].entries().next().value;
  const target_ip = targetGroup[0];
  const maxPathCount = Array.from(targetGroup.keys()).reduce((acc, probeIndex) => {
    const { count = 0 } = group.get(agentID).get(target_ip).get(probeIndex) || {};

    acc = acc > count ? acc : count;

    return acc;
  }, 0);

  return {
    name: 'Collapsed Path',
    title: 'Collapsed Path',
    textCount: maxPathCount
  };
};

const getASNData = (data) => {
  const { id, hops } = data;
  const hopWithASN = (hops || []).find((hop) => hop.asn?.id === id) || {};
  const asnName = hopWithASN?.asn.name || '';

  return {
    name: asnName,
    supText: `${hops.length} ${pluralize('hop', hops.length)}`,
    asn: {
      id,
      name: asnName
    }
  };
};

const getSiteData = (data) => {
  const siteMatch = (data?.hops || []).find((hop) => hop?.site?.id === data.id);
  const site = siteMatch?.site || {};

  return {
    name: site && site.name,
    title: 'Site',
    site
  };
};

export const buildNodeData = (data, options) => {
  let info = {};

  if (data.type === 'ip') {
    info = getIPData(data);
  }

  if (data.type === 'timeout') {
    info = getTimeoutData(data);
  }

  if (data.type === 'closed_path') {
    info = getClosedPathData(data);
  }

  if (data.type === 'agent') {
    info = getAgentData(data, options);
  }

  if (data.type === 'target') {
    info = getTargetData(data, options);
  }

  if (data.type === 'asn') {
    info = getASNData(data);
  }

  if (data.type === 'site') {
    info = getSiteData(data);
  }

  return {
    ...info,
    traceCount: getTraceCount(data.group)
  };
};

const buildLinkedNode = (node, hops) => ({
  ...node,
  hops: [...hops]
});

const buildLinkGroup = (data) => {
  const group = new Map();

  data.toHops.forEach(({ agentID, target_ip, probeIndex }) => {
    // document agents
    if (!group.has(agentID)) {
      group.set(agentID, new Map());
    }

    // document targets
    if (!group.get(agentID).has(target_ip)) {
      group.get(agentID).set(target_ip, new Map());
    }

    // document probes
    if (!group.get(agentID).get(target_ip).has(probeIndex)) {
      group.get(agentID).get(target_ip).set(probeIndex, { count: 0 });
    }

    group.get(agentID).get(target_ip).get(probeIndex).count += 1;
  });

  return group;
};

export const buildLinkData = (data, nodes, options) => {
  const fromNode = buildLinkedNode(
    nodes.find(({ id }) => data.from === id),
    data.fromHops
  );
  const toNode = buildLinkedNode(
    nodes.find(({ id }) => data.to === id),
    data.toHops
  );
  const to = buildNodeData(toNode, options);
  const from = buildNodeData(fromNode, options);
  const toMetrics = get(to, 'nodeMetrics.latency');
  const fromMetrics = fromNode.type === 'agent' ? { min: 0, max: 0, average: 0 } : get(from, 'nodeMetrics.latency');

  let linkMetrics;

  if (fromMetrics && toMetrics) {
    // avoid negative values by using the absolute delta between source and destination metrics
    linkMetrics = {
      min: Math.abs(toMetrics.min - fromMetrics.min),
      max: Math.abs(toMetrics.max - fromMetrics.max),
      average: Math.abs(toMetrics.average - fromMetrics.average),
      minExpectedLatency: toMetrics.minExpectedLatency
    };
  }

  const group = buildLinkGroup(data);
  const traceCount = getTraceCount(group);

  return {
    to: {
      ...to,
      isCircle: toNode.isCircle,
      style: toNode.style
    },
    from: {
      ...from,
      isCircle: fromNode.isCircle,
      style: fromNode.style
    },
    group,
    traceCount,
    linkMetrics
  };
};
